import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useIsMobile } from "@/hooks/use-mobile";
import { CheckIcon } from "lucide-react";

const packages = [
  {
    name: "Pyksel Launch",
    price: "1 999",
    oneTimeFee: "29 999",
    description: "For gründere og små bedrifter som ønsker en profesjonell start på nett.",
    features: [
      { text: "Passer for: Gründer / liten bedrift" },
      { text: "Design / sider: Inntil 5 sider" },
      { text: "Hosting og sikkerhet: Inkludert", description: "Webhotellet ditt er inkludert, noe som betyr at vi plasserer nettstedet ditt på vår raske og pålitelige server, slik at det alltid er tilgjengelig. Vi inkluderer også essensielle sikkerhetstiltak for å beskytte mot trusler." },
      { text: "Leveringstid: 7–10 dager" },
      { text: "Oppstartsmøte / strategi: Enkel oppstart" },
      { text: "Innhold (tekst/bilder): Kunde leverer" },
      { text: "SEO: Grunnleggende", description: "Vi optimaliserer nettsiden din slik at den blir lettere å finne på Google og andre søkemotorer." },
      { text: "Hastighetsoptimalisering: Grunnleggende" },
      { text: "Backup / sikkerhet: Ukentlig", description: "Vi tar ukentlige sikkerhetskopier av nettsiden din, slik at du kan være trygg på at innholdet ditt er sikkert." },
      { text: "Vedlikehold: 1 time/mnd", description: "Vi utfører teknisk vedlikehold hver måned for å sikre at nettsiden din fungerer som den skal." },
      { text: "Support: E-post", description: "Du får tilgang til brukerstøtte via e-post for å hjelpe deg med eventuelle spørsmål du måtte ha." },
      { text: "Ekstra funksjoner: Enkel kontaktskjema" },
      { text: "Grafisk design: Enkel profil", description: "Vi hjelper deg med en enkel visuell profil som passer din merkevare." },
      { text: "Enkel FAQ", description: "Vi setter opp en side med svar på ofte stilte spørsmål for å hjelpe dine kunder og redusere antall henvendelser." },
    ],
    notIncluded: [
        "Rapport / analyse",
    ]
  },
  {
    name: "Pyksel Expand",
    price: "3 499",
    oneTimeFee: "54 999–74 999",
    description: "For etablerte bedrifter med vekstplaner som trenger mer funksjonalitet.",
    features: [
      { text: "Passer for: Etablert bedrift med vekstplaner" },
      { text: "Design / sider: Inntil 10 sider" },
      { text: "Hosting og sikkerhet: Inkludert", description: "Webhotellet ditt er inkludert, noe som betyr at vi plasserer nettstedet ditt på vår raske og pålitelige server, slik at det alltid er tilgjengelig. Vi inkluderer også essensielle sikkerhetstiltak for å beskytte mot trusler." },
      { text: "Leveringstid: 10–14 dager" },
      { text: "Oppstartsmøte / strategi: Digital planlegging" },
      { text: "Innhold (tekst/bilder): Pyksel hjelper" },
      { text: "SEO: Utvidet + sporing", description: "Vi går dypere enn grunnleggende SEO ved å utføre søkeordsanalyse og optimalisere innholdet ditt for bedre rangering. 'Sporing' betyr at vi overvåker ytelsen din og gir deg månedlige rapporter slik at du kan se fremgangen." },
      { text: "Hastighetsoptimalisering: Optimalisert" },
      { text: "Backup / sikkerhet: Daglig", description: "Vi tar daglige sikkerhetskopier av nettsiden din for ekstra trygghet." },
      { text: "Vedlikehold: 3 timer/mnd", description: "Vi utfører teknisk vedlikehold hver måned for å sikre at nettsiden din fungerer som den skal." },
      { text: "Support: Prioritert", description: "Du får prioritert brukerstøtte for raskere svar på dine henvendelser." },
      { text: "Ekstra funksjoner: Avansert kontaktskjema" },
      { text: "Grafisk design: Profesjonell tilpasning", description: "Vi hjelper deg med en profesjonell visuell profil som er skreddersydd for din merkevare." },
      { text: "AI-funksjon: Enkel chatbot", description: "Vi setter opp en enkel chatbot på nettsiden din for å hjelpe besøkende med å finne det de leter etter." },
    ],
    notIncluded: [
        "Rapport / analyse",
    ]
  },
  {
    name: "Pyksel Beyond",
    price: "5 999",
    oneTimeFee: "89 999–129 999",
    description: "For bedrifter som vil ha alt – en komplett digital løsning.",
    features: [
      { text: "Passer for: Bedrifter som vil ha alt" },
      { text: "Design / sider: Ubegrenset sider" },
      { text: "Hosting og sikkerhet: Inkludert", description: "Webhotellet ditt er inkludert, noe som betyr at vi plasserer nettstedet ditt på vår raske og pålitelige server, slik at det alltid er tilgjengelig. Vi inkluderer også essensielle sikkerhetstiltak for å beskytte mot trusler." },
      { text: "Leveringstid: Etter avtale" },
      { text: "Oppstartsmøte / strategi: Strategimøte med designer" },
      { text: "Innhold (tekst/bilder): Full produksjon" },
      { text: "SEO: Avansert + markedsintegrasjon", description: "Dette er vår mest omfattende SEO-pakke. 'Avansert' innebærer en kontinuerlig strategi med teknisk SEO, innholdsmarkedsføring og linkbygging. 'Markedsintegrasjon' betyr at vi synkroniserer SEO-arbeidet med dine andre markedsføringskanaler for maksimal effekt." },
      { text: "Hastighetsoptimalisering: Maks ytelse + CDN" },
      { text: "Backup / sikkerhet: Daglig + versjonshistorikk", description: "Vi tar daglige sikkerhetskopier og gir deg tilgang til versjonshistorikk for full kontroll." },
      { text: "Vedlikehold: Ubegrenset", description: "Vi tar oss av alt teknisk vedlikehold, slik at du kan fokusere på din kjernevirksomhet." },
      { text: "Support: 24/7 premium", description: "Du får tilgang til vår premium brukerstøtte 24/7 for umiddelbar hjelp." },
      { text: "Ekstra funksjoner: Booking + avansert skjema" },
      { text: "Grafisk design: Full branding-pakke", description: "Vi utvikler en komplett branding-pakke som gir din merkevare et profesjonelt og helhetlig uttrykk." },
      { text: "AI-funksjon: Avansert chatbot med integrasjon", description: "Vi setter opp en avansert chatbot med integrasjon mot dine systemer for en sømløs kundeopplevelse." },
      { text: "Rapport / analyse: Full SEO + trafikkrapport", description: "Du mottar detaljerte rapporter som viser effekten av SEO-arbeidet og analyserer trafikken på nettstedet ditt. Dette gir deg verdifull innsikt i dine besøkende og hvordan de finner deg." },
    ],
    notIncluded: []
  },
];

export function Pricing() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  return (
    <section id="priser" className="container py-12 md:py-24">
      <div className="text-center">
        <h2 className="text-3xl font-bold tracking-tighter sm:text-5xl">
          Prisplaner for enhver bedrift
        </h2>
        <p className="max-w-[900px] text-muted-foreground md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed mx-auto mt-4">
          Velg den perfekte planen for deg. Alle priser er eks. mva.
        </p>
        <div className="mt-8">
          <Button
            onClick={() => navigate('/ecommerce')}
            variant="outline"
            className="border-primary text-primary hover:bg-primary hover:text-white hero-glow smooth-transition"
          >
            Se priser for E-commerce
          </Button>
        </div>
      </div>
      {isMobile ? (
        <Carousel
          opts={{
            align: "start",
          }}
          className="w-full max-w-sm md:max-w-2xl lg:max-w-4xl mx-auto"
        >
          <CarouselContent>
            {packages.map((pkg) => (
              <CarouselItem key={pkg.name} className="md:basis-1/2 lg:basis-1/3">
                <div className="p-1">
                  <Card key={pkg.name} className="flex flex-col border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 h-full">
                    <CardHeader>
                      <CardTitle>{pkg.name}</CardTitle>
                      <CardDescription>{pkg.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="flex-grow">
                      <div className="flex items-baseline justify-center">
                        <span className="text-4xl font-extrabold tracking-tight">
                          {pkg.price} kr
                        </span>
                        <span className="ml-1 text-xl font-semibold text-muted-foreground">
                          /måned
                        </span>
                      </div>
                      <p className="mt-1 text-center text-muted-foreground">
                        Engangsbetaling fra {pkg.oneTimeFee} kr
                      </p>
                      <ul className="mt-8 space-y-4">
                        {pkg.features.map((feature) => (
                          <li key={feature.text} className="flex items-start">
                            <CheckIcon className="w-5 h-5 mr-2 text-primary flex-shrink-0" />
                            <span>
                              {feature.text}
                              {feature.description && (
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button variant="link" className="p-1 ml-1 text-xs">Les mer</Button>
                                  </PopoverTrigger>
                                  <PopoverContent>
                                    <div className="p-4">
                                      <h4 className="font-semibold">{feature.text.split(':')[0]}</h4>
                                      <p className="text-sm text-gray-800">{feature.description}</p>
                                    </div>
                                  </PopoverContent>
                                </Popover>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious />
          <CarouselNext />
        </Carousel>
      ) : (
        <div className="grid grid-cols-1 gap-8 mt-12 md:grid-cols-2 lg:grid-cols-3">
          {packages.map((pkg) => (
            <Card key={pkg.name} className="flex flex-col border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 h-full">
              <CardHeader>
                <CardTitle>{pkg.name}</CardTitle>
                <CardDescription>{pkg.description}</CardDescription>
              </CardHeader>
              <CardContent className="flex-grow">
                <div className="flex items-baseline justify-center">
                  <span className="text-4xl font-extrabold tracking-tight">
                    {pkg.price} kr
                  </span>
                  <span className="ml-1 text-xl font-semibold text-muted-foreground">
                    /måned
                  </span>
                </div>
                <p className="mt-1 text-center text-muted-foreground">
                  Engangsbetaling fra {pkg.oneTimeFee} kr
                </p>
                <ul className="mt-8 space-y-4">
                  {pkg.features.map((feature) => (
                    <li key={feature.text} className="flex items-start">
                      <CheckIcon className="w-5 h-5 mr-2 text-primary flex-shrink-0" />
                      <span>
                        {feature.text}
                        {feature.description && (
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="link" className="p-1 ml-1 text-xs">Les mer</Button>
                            </PopoverTrigger>
                            <PopoverContent>
                              <div className="p-4">
                                <h4 className="font-semibold">{feature.text.split(':')[0]}</h4>
                                <p className="text-sm text-gray-800">{feature.description}</p>
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}
